import { z } from 'zod';
/**
 * Trade profile document schema — the anti-swamp guardrail.
 *
 * Profiles are pure DATA (jurisdiction/trade rules are data, not code).
 * This schema is deliberately rigid:
 *
 *   - exactly 2 estimate layouts (`sectioned`, `unit_price_schedule`);
 *   - exactly 5 intake field types (`text|number|select|boolean|measurement`);
 *   - NO conditional logic, NO nested groups, NO computed fields, NO
 *     per-trade components — every object is `.strict()`, so any attempt to
 *     smuggle extra capability into a profile document is a parse error;
 *   - assembly starters carry structure + suggested labor hours only;
 *     `unitCost` is schema-enforced `null` (product law: no invented market
 *     prices — the contractor fills costs from their own libraries).
 */
export const TRADE_PROFILE_ESTIMATE_STYLES = ['sectioned', 'unit_price_schedule'];
export const TradeProfileEstimateStyleSchema = z.enum(TRADE_PROFILE_ESTIMATE_STYLES);
export const INTAKE_FIELD_TYPES = ['text', 'number', 'select', 'boolean', 'measurement'];
export const IntakeFieldTypeSchema = z.enum(INTAKE_FIELD_TYPES);
const zKey = z
    .string()
    .min(1)
    .max(64)
    .regex(/^[a-z][a-z0-9_]*$/, 'keys are snake_case: lowercase letter first, then [a-z0-9_]');
const zLabel = z.string().min(1).max(120);
/** A measurement/pricing unit the trade works in (e.g. sf, lf, cy, ea). */
export const TradeProfileUnitSchema = z
    .object({
    key: zKey,
    label: zLabel,
})
    .strict();
/** Default estimate section scaffolded on estimate creation. */
export const TradeProfileSectionSchema = z
    .object({
    key: zKey,
    title: zLabel,
    description: z.string().max(2000).optional(),
})
    .strict();
/** Optional division scheme (GC profiles only), e.g. a MasterFormat-lite list. */
export const DivisionSchemeSchema = z
    .object({
    name: zLabel,
    divisions: z
        .array(z
        .object({
        code: z.string().min(1).max(16),
        title: zLabel,
    })
        .strict())
        .min(1),
})
    .strict();
// ------------------------------------------------- intake fields (exactly 5)
const intakeBase = {
    key: zKey,
    label: zLabel,
    required: z.boolean().default(false),
    helpText: z.string().max(500).optional(),
};
export const IntakeTextFieldSchema = z
    .object({
    ...intakeBase,
    type: z.literal('text'),
    placeholder: z.string().max(200).optional(),
    maxLength: z.number().int().min(1).max(5000).optional(),
})
    .strict();
export const IntakeNumberFieldSchema = z
    .object({
    ...intakeBase,
    type: z.literal('number'),
    min: z.number().finite().optional(),
    max: z.number().finite().optional(),
    step: z.number().finite().positive().optional(),
})
    .strict();
export const IntakeSelectFieldSchema = z
    .object({
    ...intakeBase,
    type: z.literal('select'),
    options: z
        .array(z.object({ value: zKey, label: zLabel }).strict())
        .min(1)
        .max(50),
})
    .strict();
export const IntakeBooleanFieldSchema = z
    .object({
    ...intakeBase,
    type: z.literal('boolean'),
})
    .strict();
export const IntakeMeasurementFieldSchema = z
    .object({
    ...intakeBase,
    type: z.literal('measurement'),
    /** Must reference a key in the profile's `units` list (checked by superRefine). */
    unitKey: zKey,
    min: z.number().finite().optional(),
})
    .strict();
/** The 5 field types. A discriminated union hard-rejects any other `type`. */
export const IntakeFieldSchema = z.discriminatedUnion('type', [
    IntakeTextFieldSchema,
    IntakeNumberFieldSchema,
    IntakeSelectFieldSchema,
    IntakeBooleanFieldSchema,
    IntakeMeasurementFieldSchema,
]);
// -------------------------------------------------------- starters & prompts
/** Labor role suggestion: name only — NEVER a rate (no invented prices). */
export const LaborRoleSuggestionSchema = z
    .object({
    name: zLabel,
    description: z.string().max(500).optional(),
})
    .strict();
/**
 * Assembly starter component. Structure + suggested hours only;
 * `unitCost` MUST be null — contractors price from their own libraries.
 */
export const AssemblyStarterComponentSchema = z.discriminatedUnion('kind', [
    z
        .object({
        kind: z.literal('labor'),
        description: zLabel,
        /** Suggested labor hours per assembly unit. */
        hoursPerUnit: z.number().finite().positive(),
        unitCost: z.null(),
    })
        .strict(),
    z
        .object({
        kind: z.literal('material'),
        description: zLabel,
        qtyPerUnit: z.number().finite().positive(),
        /** Waste percent applied to material quantity (0–100). */
        wastePct: z.number().finite().min(0).max(100).default(0),
        unitCost: z.null(),
    })
        .strict(),
    z
        .object({
        kind: z.literal('equipment'),
        description: zLabel,
        qtyPerUnit: z.number().finite().positive(),
        unitCost: z.null(),
    })
        .strict(),
]);
export const AssemblyStarterSchema = z
    .object({
    key: zKey,
    name: zLabel,
    description: z.string().max(1000).optional(),
    /** Must reference a key in the profile's `units` list (checked by superRefine). */
    unitKey: zKey,
    components: z.array(AssemblyStarterComponentSchema).min(1).max(30),
})
    .strict();
/**
 * Compliance prompt: a dismissible chip that can insert a ZERO-PRICED line
 * into the estimate. `ruleRefs` point at `compliance_rules.rule_key` values;
 * the surfaces that render these must show each rule's `boundary_note`.
 */
export const CompliancePromptSchema = z
    .object({
    key: zKey,
    label: zLabel,
    description: z.string().max(1000).optional(),
    ruleRefs: z.array(z.string().min(1).max(120)).min(1).max(10),
    /** The zero-priced line the chip inserts when applied. */
    line: z
        .object({
        description: z.string().min(1).max(300),
        unitKey: zKey.optional(),
    })
        .strict(),
})
    .strict();
// ---------------------------------------------------------------- the profile
export const TradeProfileFlagsSchema = z
    .object({
    supportsPrevailingWage: z.boolean(),
    usesCrewPricing: z.boolean(),
})
    .strict();
export const TradeProfileSchema = z
    .object({
    key: zKey,
    name: zLabel,
    version: z.number().int().min(1),
    isGeneric: z.boolean(),
    estimateStyle: TradeProfileEstimateStyleSchema,
    units: z.array(TradeProfileUnitSchema).min(1).max(40),
    sections: z.array(TradeProfileSectionSchema).min(1).max(40),
    /** GC profiles only; presentational grouping, no logic. */
    divisionScheme: DivisionSchemeSchema.optional(),
    intakeFields: z.array(IntakeFieldSchema).max(30),
    laborRoleSuggestions: z.array(LaborRoleSuggestionSchema).max(30),
    materialCategories: z.array(zLabel).max(40),
    assemblyStarters: z.array(AssemblyStarterSchema).max(60),
    compliancePrompts: z.array(CompliancePromptSchema).max(20),
    flags: TradeProfileFlagsSchema,
})
    .strict()
    .superRefine((profile, ctx) => {
    const unitKeys = new Set(profile.units.map((u) => u.key));
    const requireUnique = (label, keys, path) => {
        const seen = new Set();
        keys.forEach((key, i) => {
            if (seen.has(key)) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: `duplicate ${label} key "${key}"`,
                    path: [...path, i],
                });
            }
            seen.add(key);
        });
    };
    requireUnique('unit', profile.units.map((u) => u.key), ['units']);
    requireUnique('section', profile.sections.map((s) => s.key), ['sections']);
    requireUnique('intake field', profile.intakeFields.map((f) => f.key), ['intakeFields']);
    requireUnique('assembly starter', profile.assemblyStarters.map((a) => a.key), ['assemblyStarters']);
    requireUnique('compliance prompt', profile.compliancePrompts.map((p) => p.key), ['compliancePrompts']);
    profile.intakeFields.forEach((field, i) => {
        if (field.type === 'measurement' && !unitKeys.has(field.unitKey)) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: `measurement field "${field.key}" references unknown unit "${field.unitKey}"`,
                path: ['intakeFields', i, 'unitKey'],
            });
        }
    });
    profile.assemblyStarters.forEach((starter, i) => {
        if (!unitKeys.has(starter.unitKey)) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: `assembly starter "${starter.key}" references unknown unit "${starter.unitKey}"`,
                path: ['assemblyStarters', i, 'unitKey'],
            });
        }
    });
    profile.compliancePrompts.forEach((prompt, i) => {
        if (prompt.line.unitKey !== undefined && !unitKeys.has(prompt.line.unitKey)) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: `compliance prompt "${prompt.key}" references unknown unit "${prompt.line.unitKey}"`,
                path: ['compliancePrompts', i, 'line', 'unitKey'],
            });
        }
    });
});
/** Parse + validate a trade profile document; throws ZodError on any violation. */
export function validateTradeProfile(doc) {
    return TradeProfileSchema.parse(doc);
}
//# sourceMappingURL=schema.js.map